import { catchError, concat, EMPTY, map, mergeScan, Observable, of, startWith } from 'rxjs';
import { DsmCatalogOption, DsmCatalogPackage, DsmCatalogPageSection } from '../models/dsm-options-catalog.models';

export enum DsmCatalogListStatus {
  Loading = 'loading',
  Loaded = 'loaded',
  Error = 'error'
}

/** The rows loaded so far for one catalog query, and the sections they fall into. */
export interface DsmCatalogListing {
  items: DsmCatalogOption[];
  totalCount: number;
  sections: DsmCatalogPageSection[];
  hasMore: boolean;
  status: DsmCatalogListStatus;
}

export interface DsmCatalogSubgroup {
  key: string;
  label: string;
  options: DsmCatalogOption[];
}

export interface DsmCatalogGroup {
  key: string;
  label: string;
  subgroups: DsmCatalogSubgroup[];
}

/** A section of the list with its loaded rows laid out under Category → Subcategory headers. */
export interface DsmCatalogListSection {
  key: string;
  title: string;
  allModels: boolean;
  totalCount: number;
  groups: DsmCatalogGroup[];
  /** The plan's packages, given once all of the section's rows are loaded so they close the section as in the PDF. */
  packages: DsmCatalogPackage[];
}

type Step = DsmCatalogListing & { nextPage: number };

/**
 * Loads a catalog query page by page: the first page straight away, then one more page each time
 * `more$` emits, appended to what is already there. Pages load one at a time and stop once every
 * row is in. A failed page keeps the rows already loaded and is retried on the next `more$`.
 *
 * One query only — the page switches to a fresh `pageCatalog` when the query changes, which also
 * drops any page still in flight for the old one.
 */
export function pageCatalog(
  fetchPage: (
    pageNumber: number
  ) => Observable<{ items: DsmCatalogOption[]; totalCount: number; sections: DsmCatalogPageSection[] }>,
  more$: Observable<unknown>
): Observable<DsmCatalogListing> {
  const first: Step = {
    items: [],
    totalCount: 0,
    sections: [],
    hasMore: true,
    status: DsmCatalogListStatus.Loading,
    nextPage: 1
  };

  return more$.pipe(
    startWith(undefined),
    mergeScan(
      (step: Step) =>
        step.hasMore
          ? concat(
              of({ ...step, status: DsmCatalogListStatus.Loading }),
              fetchPage(step.nextPage).pipe(
                map(({ items, totalCount, sections }) => {
                  const loaded = [...step.items, ...items];
                  return {
                    items: loaded,
                    totalCount,
                    // packages come with page 1 only; later pages send the same sections without them
                    sections: sections.map((section, i) => ({
                      ...section,
                      packages: section.packages ?? step.sections[i]?.packages ?? null
                    })),
                    hasMore: loaded.length < totalCount && items.length > 0,
                    status: DsmCatalogListStatus.Loaded,
                    nextPage: step.nextPage + 1
                  };
                }),
                catchError(() => of({ ...step, status: DsmCatalogListStatus.Error }))
              )
            )
          : EMPTY,
      first,
      1
    ),
    map(({ nextPage: _, ...listing }) => listing)
  );
}

/**
 * Lays rows out under Category → Subcategory headers. The API sends a subcategory's rows together
 * (category, then subcategory, then code), so one pass that opens a new header whenever the id
 * changes is enough — and a subcategory split across two pages still lands under one header.
 */
export function groupCatalogRows(rows: DsmCatalogOption[]): DsmCatalogGroup[] {
  const groups: DsmCatalogGroup[] = [];

  rows.forEach(option => {
    const categoryKey = option.categoryId ?? '';
    const subcategoryKey = `${categoryKey}/${option.subcategoryId ?? ''}`;

    let group = groups[groups.length - 1];
    if (group?.key !== categoryKey) {
      group = { key: categoryKey, label: option.categoryLabel ?? '', subgroups: [] };
      groups.push(group);
    }

    let subgroup = group.subgroups[group.subgroups.length - 1];
    if (subgroup?.key !== subcategoryKey) {
      subgroup = { key: subcategoryKey, label: option.subcategoryLabel ?? '', options: [] };
      group.subgroups.push(subgroup);
    }

    subgroup.options.push(option);
  });

  return groups;
}

/**
 * Splits the loaded rows into their sections — each section owns the next `totalCount` rows of the
 * paged list — and lays each out under Category → Subcategory headers. A section shows once the rows
 * loaded so far reach its place, so an empty one appears between its neighbours. Header keys carry the
 * section, so folding a category in one section leaves the same category open in the others.
 */
export function sectionCatalogRows(
  rows: DsmCatalogOption[],
  sections: DsmCatalogPageSection[]
): DsmCatalogListSection[] {
  let start = 0;

  return sections.flatMap(({ title, allModels, totalCount, packages }, index) => {
    const from = start;
    start += totalCount;
    if (from > rows.length) return [];

    const key = `${index}`;
    const groups = groupCatalogRows(rows.slice(from, from + totalCount)).map(group => ({
      ...group,
      key: `${key}/${group.key}`,
      subgroups: group.subgroups.map(subgroup => ({ ...subgroup, key: `${key}/${subgroup.key}` }))
    }));

    const complete = rows.length >= from + totalCount;

    return [{ key, title, allModels, totalCount, groups, packages: complete ? (packages ?? []) : [] }];
  });
}
